import React, { useEffect, useState, useRef } from 'react';
import { FaClock } from 'react-icons/fa';
import { StatusPill } from '../../styles/techComponents';
import { getCronSchedule } from '../../services/cronScheduleService';

// Cache compartido entre instancias para no spamear /api/cron/schedule.
// TTL corto (60s) — el "next_run_at" cambia muy lento (1 vez/día), pero
// queremos detectar reseteos después de que el cron corrió.
const CACHE_TTL_MS = 60_000;
let _cache = null; // { data, fetchedAt, promise }

async function fetchScheduleCached() {
  const now = Date.now();
  if (_cache && now - _cache.fetchedAt < CACHE_TTL_MS) {
    return _cache.promise;
  }
  const promise = getCronSchedule().then((data) => {
    _cache = { data, fetchedAt: Date.now(), promise: Promise.resolve(data) };
    return data;
  }).catch((err) => {
    _cache = null; // permite reintentar
    throw err;
  });
  _cache = { data: null, fetchedAt: now, promise };
  return promise;
}

function fmtCountdown(ms) {
  if (ms == null) return '—';
  if (ms <= 0) return 'ya mismo';
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

// Pill compacta. Muestra "Próximo scraping: 02:14:33" con tick cada segundo.
// Si el cron no se encuentra, no renderiza nada (silent fail — no rompe el header).
const CronCountdown = ({ cronKey, label = 'Próxima actualización' }) => {
  const [cronInfo, setCronInfo] = useState(null);
  const [tick, setTick] = useState(0); // fuerza re-render cada segundo
  const intervalRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchScheduleCached()
      .then((data) => {
        if (cancelled) return;
        const match = data?.crons?.find((c) => c.key === cronKey);
        setCronInfo(match || null);
      })
      .catch(() => {
        if (!cancelled) setCronInfo(null);
      });
    return () => { cancelled = true; };
  }, [cronKey]);

  useEffect(() => {
    intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  // Si pasó el next_run_at, refresca el schedule del backend (el cron ya disparó).
  useEffect(() => {
    if (!cronInfo?.next_run_at) return;
    const ms = new Date(cronInfo.next_run_at).getTime() - Date.now();
    if (ms <= 0) {
      // invalidar cache y volver a pedir
      _cache = null;
      fetchScheduleCached()
        .then((data) => {
          const match = data?.crons?.find((c) => c.key === cronKey);
          if (match) setCronInfo(match);
        })
        .catch(() => {});
    }
  }, [cronInfo, cronKey, tick]);

  if (!cronInfo?.next_run_at) return null;
  const ms = new Date(cronInfo.next_run_at).getTime() - Date.now();
  return (
    <StatusPill>
      <FaClock style={{ marginRight: 6, opacity: 0.7 }} />
      {label} <strong>{fmtCountdown(ms)}</strong>
    </StatusPill>
  );
};

export default CronCountdown;
